/**
 * The edit panel. Entirely schema-driven — it has never heard of a debtor or a
 * mailbox, only of fields, rules and lookups.
 *
 * Three behaviours are worth knowing about, because each exists to stop a
 * specific thing going wrong:
 *
 *  1. **Implications apply as you type.** Ticking forwarding ticks tagging,
 *     because `CK_..._fwd_implies_tag` would otherwise reject the save after
 *     the user had already filled the form in.
 *  2. **Autofill respects an edit.** Picking a zone fills the window from that
 *     zone's defaults, but never overwrites a window the user has touched. The
 *     stored hours are the effective hours, so the user's value has to win.
 *  3. **Server field errors land on the fields.** A 422 carries `fields`, and
 *     those are merged with local validation rather than shown as a toast.
 */

import { useMemo, useRef, useState } from 'react'
import { useQueries } from '@tanstack/react-query'
import { api, ApiError } from '../api'
import { useAvailableKeys, useRowHistory, useSaveRow, useDeleteRow } from '../api/hooks'
import type { ConfigRow, ConfigTypeSchema, FieldSchema, LookupField } from '../api/types'
import { FieldInput } from '../fields/FieldInput'
import { applyImplications, isRequiredNow, transformKey, validateKey, validateRow, type FieldErrors } from '../lib/validate'
import { formatTimestamp, shortenUpn } from '../lib/format'
import { Drawer, ConfirmDialog } from './Drawer'
import { ErrorBanner } from './feedback'
import { HistoryPanel } from './HistoryPanel'
import { useToasts } from './toast-context'

function initialValues(schema: ConfigTypeSchema, row: ConfigRow | null): ConfigRow {
  if (row) return { ...row }
  const values: ConfigRow = {}
  for (const field of schema.fields) {
    if (!field.editable) continue
    values[field.name] = field.default ?? (field.type === 'bool' ? false : null)
  }
  return values
}

/** Fetches every lookup source this config type references, in one place. */
function useLookupSources(schema: ConfigTypeSchema) {
  const sources = useMemo(
    () =>
      Array.from(
        new Set(
          schema.fields
            .filter((f): f is LookupField => f.type === 'lookup')
            .map((f) => f.source),
        ),
      ),
    [schema],
  )

  const results = useQueries({
    queries: sources.map((source) => ({
      queryKey: ['config', source, 'lookup'],
      queryFn: () => api.list(source, { page_size: 500 }),
      staleTime: 5 * 60 * 1000,
    })),
  })

  return useMemo(() => {
    const map: Record<string, ConfigRow[]> = {}
    sources.forEach((source, index) => {
      map[source] = results[index]?.data?.rows ?? []
    })
    return map
  }, [sources, results])
}

export function RowEditor({
  schema,
  row,
  onClose,
}: {
  schema: ConfigTypeSchema
  /** Null for a new row. */
  row: ConfigRow | null
  onClose: () => void
}) {
  const isNew = row === null
  const readOnly = !schema.permissions.write
  const toasts = useToasts()

  const [keyValue, setKeyValue] = useState(() =>
    isNew ? '' : String(row[schema.key_field.name] ?? ''),
  )
  const [values, setValues] = useState<ConfigRow>(() => initialValues(schema, row))
  const [errors, setErrors] = useState<FieldErrors>({})
  const [keyError, setKeyError] = useState<string | null>(null)
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  /** Fields the user has typed into, so autofill leaves them alone. */
  const touched = useRef<Set<string>>(new Set())

  const lookups = useLookupSources(schema)
  const save = useSaveRow(schema.key)
  const remove = useDeleteRow(schema.key)

  const rowKey = isNew ? keyValue : String(row[schema.key_field.name] ?? '')
  const pickFromParent = isNew && Boolean(schema.key_field.available_keys)
  const available = useAvailableKeys(schema.key, pickFromParent)
  const history = useRowHistory(schema.key, isNew ? null : rowKey, !isNew)

  const handleChange = (name: string, value: unknown) => {
    touched.current.add(name)
    setValues((current) => applyImplications(schema, { ...current, [name]: value }))
    setErrors((current) => {
      if (!current[name]) return current
      const { [name]: _removed, ...rest } = current
      return rest
    })
  }

  const handleLookupPicked = (field: FieldSchema, sourceRow: ConfigRow | null) => {
    if (field.type !== 'lookup' || !field.autofill) return
    // The lookup field itself was just set by handleChange; this only copies
    // the source row's values across into the targets.
    setValues((current) => {
      const next = { ...current }
      for (const [target, sourceField] of Object.entries(field.autofill ?? {})) {
        if (touched.current.has(target)) continue // Never clobber an edit.
        next[target] = sourceRow ? (sourceRow[sourceField] ?? null) : null
      }
      return applyImplications(schema, next)
    })
  }

  const serverErrors = save.error instanceof ApiError ? (save.error.fields ?? {}) : {}
  const allErrors: FieldErrors = { ...errors, ...serverErrors }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (readOnly) return

    const key = transformKey(schema.key_field, keyValue)
    if (isNew) {
      const problem = validateKey(schema.key_field, key)
      setKeyError(problem)
      if (problem) return
    }

    const found = validateRow(schema, values)
    setErrors(found)
    if (Object.keys(found).length > 0) return

    // Only editable fields go up. The server rejects anything else rather than
    // ignoring it, so sending the whole row back would fail the save.
    const body: ConfigRow = {}
    for (const field of schema.fields) {
      if (field.editable) body[field.name] = values[field.name] ?? null
    }

    try {
      await save.mutateAsync({ key, values: body })
      toasts.success(
        isNew ? 'Row created' : 'Changes saved',
        `${schema.label} · ${key}`,
        schema.save_note,
      )
      onClose()
    } catch {
      // Rendered inline from `save.error`; nothing useful to add here.
    }
  }

  const handleDelete = async () => {
    try {
      await remove.mutateAsync(rowKey)
      toasts.success(
        schema.soft_delete ? 'Row deactivated' : 'Row deleted',
        rowKey,
        schema.save_note,
      )
      onClose()
    } catch (error) {
      toasts.error(
        'Could not delete',
        error instanceof ApiError ? (error.detail ?? error.message) : 'Unexpected error',
      )
      setConfirmingDelete(false)
    }
  }

  const editableFields = schema.fields.filter((f) => f.editable)
  const readOnlyFields = schema.fields.filter(
    (f) => !f.editable && f.name !== 'updated_at' && f.name !== 'updated_by',
  )

  if (confirmingDelete) {
    return (
      <ConfirmDialog
        title={schema.soft_delete ? `Deactivate ${rowKey}?` : `Delete ${rowKey}?`}
        confirmLabel={schema.soft_delete ? 'Deactivate' : 'Delete'}
        busy={remove.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmingDelete(false)}
        body={
          schema.soft_delete ? (
            <p>
              The row stays in the table with its history intact, but stops being
              approved. Nothing will be forwarded for it from the next config refresh.
            </p>
          ) : (
            <p>
              The row is removed. The change is recorded in the audit table, so it stays
              answerable who did it and when.
            </p>
          )
        }
      />
    )
  }

  return (
    <Drawer
      title={isNew ? `New ${schema.label.toLowerCase()}` : schema.label}
      subtitle={isNew ? undefined : rowKey}
      onClose={onClose}
      footer={
        readOnly ? (
          <>
            <span className="field__help" style={{ margin: 0 }}>
              You have read-only access to {schema.label.toLowerCase()}.
            </span>
            <button type="button" className="btn toolbar__spacer" onClick={onClose}>
              Close
            </button>
          </>
        ) : (
          <>
            <button
              type="submit"
              form="row-editor"
              className="btn btn--primary"
              disabled={save.isPending}
            >
              {save.isPending ? 'Saving…' : isNew ? 'Create' : 'Save changes'}
            </button>
            <button type="button" className="btn" onClick={onClose} disabled={save.isPending}>
              Cancel
            </button>
            {!isNew && (schema.can_delete || schema.soft_delete) ? (
              <button
                type="button"
                className="btn btn--danger toolbar__spacer"
                onClick={() => setConfirmingDelete(true)}
              >
                {schema.soft_delete ? 'Deactivate' : 'Delete'}
              </button>
            ) : null}
          </>
        )
      }
    >
      {save.error && Object.keys(serverErrors).length === 0 ? (
        <ErrorBanner error={save.error} context="The change was not saved." />
      ) : null}

      <form id="row-editor" onSubmit={handleSubmit}>
        {isNew ? (
          <div className="field">
            <label className="field__label" htmlFor="row-key">
              {schema.key_field.label}
              <span className="field__required">*</span>
            </label>
            {pickFromParent ? (
              // Only keys that exist in the parent area and have no row here
              // yet (estate mailboxes not configured for ATI), from the API.
              <>
                <select
                  id="row-key"
                  className={`select ${keyError ? 'select--error' : ''}`}
                  value={keyValue}
                  autoFocus
                  disabled={available.isLoading || (available.data?.keys.length ?? 0) === 0}
                  onChange={(event) => setKeyValue(event.target.value)}
                >
                  <option value="" disabled>
                    {available.isLoading ? 'Loading…' : 'Choose…'}
                  </option>
                  {available.data?.keys.map((key) => (
                    <option key={key} value={key}>
                      {key}
                    </option>
                  ))}
                </select>
                {available.data && available.data.keys.length === 0 ? (
                  <p className="field__error">
                    Every mailbox in the estate already has a row here. A global admin has to
                    add a new mailbox to the mailbox estate first.
                  </p>
                ) : null}
                <ErrorBanner error={available.error} context="Could not load the mailboxes." />
              </>
            ) : schema.key_field.options ? (
              // A closed list (IANA timezones): nothing to mistype.
              <select
                id="row-key"
                className={`select ${keyError ? 'select--error' : ''}`}
                value={keyValue}
                autoFocus
                onChange={(event) => setKeyValue(event.target.value)}
              >
                <option value="" disabled>
                  Choose…
                </option>
                {schema.key_field.options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                id="row-key"
                className={`input ${keyError ? 'input--error' : ''}`}
                value={keyValue}
                maxLength={schema.key_field.max_len}
                autoFocus
                onChange={(event) =>
                  setKeyValue(transformKey(schema.key_field, event.target.value))
                }
              />
            )}
            {schema.key_field.help ? (
              <p className="field__help">{schema.key_field.help}</p>
            ) : null}
            {keyError ? <p className="field__error">{keyError}</p> : null}
          </div>
        ) : null}

        {editableFields.map((field) => (
          <FieldInput
            key={field.name}
            field={field}
            value={values[field.name]}
            error={allErrors[field.name]}
            required={isRequiredNow(schema, field, values)}
            disabled={readOnly || save.isPending}
            lookupRows={field.type === 'lookup' ? lookups[field.source] : undefined}
            onChange={handleChange}
            onLookupPicked={handleLookupPicked}
          />
        ))}

        {readOnlyFields.length > 0 && !isNew ? (
          <div className="section">
            <h3 className="section__title">Recorded automatically</h3>
            {readOnlyFields.map((field) => (
              <FieldInput
                key={field.name}
                field={field}
                value={values[field.name]}
                disabled
                onChange={() => {}}
              />
            ))}
          </div>
        ) : null}
      </form>

      {!isNew ? (
        <>
          <div className="section">
            <h3 className="section__title">Last change</h3>
            <div className="stamp">
              <span>{formatTimestamp(row?.updated_at)}</span>
              <span>by {shortenUpn(row?.updated_by)}</span>
            </div>
          </div>

          <div className="section">
            <h3 className="section__title">History</h3>
            <HistoryPanel
              schema={schema}
              entries={history.data?.entries}
              isLoading={history.isLoading}
            />
          </div>
        </>
      ) : null}
    </Drawer>
  )
}
